package io.github.dautovicharis.charts.app.ui.composable

import androidx.compose.material3.ColorScheme
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.ui.graphics.Color
import chartsproject.app.generated.resources.Res
import chartsproject.app.generated.resources.style_auto_generated_from_empty
import chartsproject.app.generated.resources.style_fallback_name
import chartsproject.app.generated.resources.style_name_custom
import chartsproject.app.generated.resources.style_name_default
import chartsproject.app.generated.resources.style_unused_generic_usage
import chartsproject.app.generated.resources.style_unused_ignored_because_provided
import chartsproject.app.generated.resources.style_value_auto
import chartsproject.app.generated.resources.style_value_color
import chartsproject.app.generated.resources.style_value_colors_count
import chartsproject.app.generated.resources.style_value_custom
import chartsproject.app.generated.resources.style_value_default
import io.github.dautovicharis.charts.style.Style
import org.jetbrains.compose.resources.stringResource
import kotlin.math.abs
import kotlin.math.roundToInt

typealias ColorNameResolver = (Color) -> ColorDisplay

data class ColorDisplay(
    val value: String,
    val details: String? = null,
)

data class StyleItems(
    val name: String,
    val items: List<StyleItem>,
)

data class StyleItem(
    val name: String,
    val value: String,
    val extraInfo: String? = null,
    val color: Color? = null,
    val colors: List<Color> = emptyList(),
    val isChanged: Boolean,
    val unusedReason: String? = null,
    val status: StyleItemStatus = StyleItemStatus.Default,
)

enum class StyleItemStatus {
    Modified,
    Default,
    Unused,
}

@Composable
fun ChartStyleItems(
    currentStyle: Style,
    defaultStyle: Style,
): StyleItems {
    val resolver =
        materialColorNameResolver(
            colorScheme = MaterialTheme.colorScheme,
            fallbackColorName = stringResource(Res.string.style_value_color),
        )
    return buildStyleItems(
        currentStyle = currentStyle,
        defaultStyle = defaultStyle,
        colorNameResolver = resolver,
    )
}

@Composable
fun buildStyleItems(
    currentStyle: Style,
    defaultStyle: Style,
    colorNameResolver: ColorNameResolver,
): StyleItems {
    val currentProperties = currentStyle.getProperties()
    val currentPropertiesMap = currentProperties.toMap()
    val defaultProperties = defaultStyle.getProperties().toMap()

    val items =
        currentProperties.map { (name, currentValue) ->
            val defaultValue = defaultProperties[name]
            val isChanged = currentValue != defaultValue
            val unusedReason =
                resolveUnusedReason(
                    currentStyle = currentStyle,
                    propertyName = name,
                    currentProperties = currentPropertiesMap,
                )
            val status = resolveStyleItemStatus(isChanged = isChanged, unusedReason = unusedReason)

            when (currentValue) {
                is List<*> -> {
                    val colorValues = currentValue.filterIsInstance<Color>()
                    val hasOnlyColors = currentValue.all { it is Color }
                    val autoGeneratedFrom =
                        resolveAutoGeneratedColorSource(
                            propertyName = name,
                            currentProperties = currentPropertiesMap,
                            hasOnlyColors = hasOnlyColors,
                            colorValues = colorValues,
                        )
                    val value =
                        when {
                            autoGeneratedFrom != null -> stringResource(Res.string.style_value_auto)
                            hasOnlyColors ->
                                stringResource(
                                    Res.string.style_value_colors_count,
                                    colorValues.size,
                                )
                            isChanged -> stringResource(Res.string.style_value_custom)
                            else -> stringResource(Res.string.style_value_default)
                        }
                    val extraInfo =
                        autoGeneratedFrom?.let { basePropertyName ->
                            stringResource(
                                Res.string.style_auto_generated_from_empty,
                                basePropertyName,
                                name,
                            )
                        }
                    StyleItem(
                        name = name,
                        value = value,
                        extraInfo = extraInfo,
                        colors = if (hasOnlyColors) colorValues else emptyList(),
                        isChanged = isChanged,
                        unusedReason = unusedReason,
                        status = status,
                    )
                }
                is Color -> {
                    val colorDisplay = colorNameResolver(currentValue)
                    StyleItem(
                        name = name,
                        value = colorDisplay.value,
                        extraInfo = colorDisplay.details,
                        color = currentValue,
                        isChanged = isChanged,
                        unusedReason = unusedReason,
                        status = status,
                    )
                }
                else -> {
                    StyleItem(
                        name = name,
                        value = currentValue.toString(),
                        isChanged = isChanged,
                        unusedReason = unusedReason,
                        status = status,
                    )
                }
            }
        }

    val activeItems =
        items
            .withIndex()
            .filter { it.value.status != StyleItemStatus.Unused }
            .sortedWith(
                compareBy<IndexedValue<StyleItem>>(
                    { statusSortOrder(it.value.status) },
                    { it.index },
                ),
            ).map { it.value }
    val unusedItems = items.filter { it.status == StyleItemStatus.Unused }
    val sortedItems = activeItems + unusedItems
    val hasCustomValues = sortedItems.any { it.status == StyleItemStatus.Modified }
    val baseName = currentStyle::class.simpleName ?: stringResource(Res.string.style_fallback_name)
    val styleName =
        stringResource(
            if (hasCustomValues) Res.string.style_name_custom else Res.string.style_name_default,
            baseName,
        )
    return StyleItems(
        name = styleName,
        items = sortedItems,
    )
}

private fun materialColorNameResolver(
    colorScheme: ColorScheme,
    fallbackColorName: String,
): ColorNameResolver {
    val colorNames =
        mapOf(
            colorScheme.primary to "MaterialTheme.colorScheme.primary",
            colorScheme.secondary to "MaterialTheme.colorScheme.secondary",
            colorScheme.tertiary to "MaterialTheme.colorScheme.tertiary",
            colorScheme.background to "MaterialTheme.colorScheme.background",
            colorScheme.surface to "MaterialTheme.colorScheme.surface",
            colorScheme.error to "MaterialTheme.colorScheme.error",
            colorScheme.onPrimary to "MaterialTheme.colorScheme.onPrimary",
            colorScheme.onSecondary to "MaterialTheme.colorScheme.onSecondary",
            colorScheme.onBackground to "MaterialTheme.colorScheme.onBackground",
            colorScheme.onSurface to "MaterialTheme.colorScheme.onSurface",
            colorScheme.onError to "MaterialTheme.colorScheme.onError",
        )

    return { color ->
        val token = colorNames[color]
        if (token == null) {
            resolveAlphaDerivedColorDisplay(
                color = color,
                colorNames = colorNames,
            ) ?: ColorDisplay(fallbackColorName)
        } else {
            ColorDisplay(
                value = token.substringAfterLast('.'),
                details = token,
            )
        }
    }
}

private fun resolveAlphaDerivedColorDisplay(
    color: Color,
    colorNames: Map<Color, String>,
): ColorDisplay? {
    val alphaDerivedToken =
        colorNames.entries
            .firstOrNull { (baseColor, _) ->
                hasSameRgb(baseColor, color) && !isClose(baseColor.alpha, color.alpha)
            }?.value ?: return null

    val alphaPercent = (color.alpha * 100f).roundToInt()
    val alphaValue = formatFloat(color.alpha)
    return ColorDisplay(
        value = "${alphaDerivedToken.substringAfterLast('.')} ($alphaPercent%)",
        details = "$alphaDerivedToken.copy(alpha = ${alphaValue}f)",
    )
}

private fun hasSameRgb(
    first: Color,
    second: Color,
): Boolean =
    isClose(first.red, second.red) &&
        isClose(first.green, second.green) &&
        isClose(first.blue, second.blue)

private fun isClose(
    first: Float,
    second: Float,
): Boolean = abs(first - second) < 0.0001f

private fun formatFloat(value: Float): String {
    val rounded = (value * 100f).roundToInt() / 100f
    val roundedAsInt = rounded.toInt()
    return if (rounded == roundedAsInt.toFloat()) roundedAsInt.toString() else rounded.toString()
}

@Composable
private fun resolveUnusedReason(
    currentStyle: Style,
    propertyName: String,
    currentProperties: Map<String, Any>,
): String? {
    val pairedColorsPropertyName = "${propertyName}s"
    val hasExplicitColorList =
        propertyName.endsWith("Color") &&
            ((currentProperties[pairedColorsPropertyName] as? List<*>)?.isNotEmpty() == true)

    return when {
        hasExplicitColorList && propertyName !in setOf("lineColor", "areaColor") -> {
            val usageClause =
                stringResource(
                    Res.string.style_unused_generic_usage,
                    propertyName,
                    pairedColorsPropertyName,
                )
            stringResource(
                Res.string.style_unused_ignored_because_provided,
                pairedColorsPropertyName,
                usageClause,
            )
        }

        else -> null
    }
}

private fun resolveAutoGeneratedColorSource(
    propertyName: String,
    currentProperties: Map<String, Any>,
    hasOnlyColors: Boolean,
    colorValues: List<Color>,
): String? {
    if (!hasOnlyColors || colorValues.isNotEmpty() || !propertyName.endsWith("Colors")) {
        return null
    }

    val basePropertyName = propertyName.removeSuffix("s")
    return if (currentProperties[basePropertyName] is Color) basePropertyName else null
}

private fun resolveStyleItemStatus(
    isChanged: Boolean,
    unusedReason: String?,
): StyleItemStatus =
    when {
        !unusedReason.isNullOrBlank() -> StyleItemStatus.Unused
        isChanged -> StyleItemStatus.Modified
        else -> StyleItemStatus.Default
    }

private fun statusSortOrder(status: StyleItemStatus): Int =
    when (status) {
        StyleItemStatus.Modified -> 0
        StyleItemStatus.Default -> 1
        StyleItemStatus.Unused -> 2
    }
